/**
 * NavigatorX Hazard Manager
 * 
 * Dashboard for managing hazard reports:
 * - Active hazards overview
 * - Hazard moderation (approve/reject/delete)
 * - Reporter management
 * - Hazard analytics
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  AlertTriangle,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  RefreshCw,
  Loader2,
  Users,
  BarChart3,
  Calendar,
  Eye,
  EyeOff,
  Search,
  Award,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../../../utils/firebase/client';
import { getActiveHazards, approveHazard, deleteHazard, expireHazard } from '../../../utils/services/navigatorXService';
import type { Hazard, HazardType } from '../../../types/navigatorx';

interface HazardManagerProps {
  embedded?: boolean;
}

const HAZARD_TYPES: Record<HazardType, { label: string; icon: string; color: string }> = {
  road_closure: { label: 'Road Closure', icon: '🚧', color: 'bg-red-100 text-red-800' },
  flood: { label: 'Flood', icon: '🌊', color: 'bg-blue-100 text-blue-800' },
  ice: { label: 'Ice', icon: '🧊', color: 'bg-cyan-100 text-cyan-800' },
  construction: { label: 'Construction', icon: '🏗️', color: 'bg-orange-100 text-orange-800' },
  accident: { label: 'Accident', icon: '💥', color: 'bg-red-100 text-red-800' },
  wildlife: { label: 'Wildlife', icon: '🦌', color: 'bg-green-100 text-green-800' },
  equipment: { label: 'Equipment', icon: '🔧', color: 'bg-yellow-100 text-yellow-800' },
  other: { label: 'Other', icon: '⚠️', color: 'bg-gray-100 text-gray-800' }
};

interface HazardStats {
  total: number;
  active: number;
  pending: number;
  byType: Record<string, number>;
  topReporters: { userId: string; count: number }[];
}

export default function HazardManager({ embedded = false }: HazardManagerProps) {
  const [loading, setLoading] = useState(true);
  const [hazards, setHazards] = useState<Hazard[]>([]);
  const [stats, setStats] = useState<HazardStats | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'pending'>('active');
  const [typeFilter, setTypeFilter] = useState<HazardType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedHazard, setExpandedHazard] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'hazards' | 'reporters' | 'analytics'>('hazards');

  const adminId = auth.currentUser?.uid || 'unknown';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const allHazards = await getActiveHazards();
      setHazards(allHazards);

      const byType: Record<string, number> = {};
      const reporterCounts: Record<string, number> = {};
      let active = 0, pending = 0;

      allHazards.forEach(h => {
        byType[h.type] = (byType[h.type] || 0) + 1;
        if (h.status === 'active') active++;
        if (h.status === 'pending') pending++;
        if (h.reportedBy) reporterCounts[h.reportedBy] = (reporterCounts[h.reportedBy] || 0) + 1;
      });

      const topReporters = Object.entries(reporterCounts)
        .map(([userId, count]) => ({ userId, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setStats({ total: allHazards.length, active, pending, byType, topReporters });
    } catch (error) {
      console.error('Error loading hazards:', error);
      toast.error('Failed to load hazards');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try { await approveHazard(id, adminId); toast.success('Approved'); loadData(); }
    catch (e: any) { toast.error(e.message || 'Failed'); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete?')) return;
    try { await deleteHazard(id, adminId); toast.success('Deleted'); loadData(); }
    catch (e: any) { toast.error(e.message || 'Failed'); }
  };

  const handleExpire = async (id: string) => {
    try { await expireHazard(id, adminId); toast.success('Expired'); loadData(); }
    catch (e: any) { toast.error(e.message || 'Failed'); }
  };

  const formatDate = (ts: any) => {
    if (!ts) return 'Unknown';
    const d = ts instanceof Timestamp ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  };

  const getTimeRemaining = (exp: any) => {
    if (!exp) return null;
    const d = exp instanceof Timestamp ? exp.toDate() : new Date(exp);
    const diff = d.getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const filteredHazards = hazards
    .filter(h => filter === 'all' || h.status === filter)
    .filter(h => typeFilter === 'all' || h.type === typeFilter)
    .filter(h => !searchTerm || h.description?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                h.location?.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Hazard Manager</h2>
            <p className="text-gray-500">Review and moderate hazard reports</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className={stats?.pending ? 'border-orange-200' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Pending Review</p>
                <p className={`text-2xl font-bold ${stats?.pending ? 'text-orange-600' : 'text-gray-700'}`}>
                  {stats?.pending || 0}
                </p>
              </div>
              <Clock className={`h-8 w-8 ${stats?.pending ? 'text-orange-300' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Active Hazards</p>
                <p className="text-2xl font-bold text-red-600">{stats?.active || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Reports</p>
                <p className="text-2xl font-bold text-gray-700">{stats?.total || 0}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Top Reporters</p>
                <p className="text-2xl font-bold text-gray-700">{stats?.topReporters.length || 0}</p>
              </div>
              <Users className="h-8 w-8 text-gray-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b pb-2">
        {[
          { id: 'hazards', label: 'Hazards', icon: AlertTriangle },
          { id: 'reporters', label: 'Top Reporters', icon: Award },
          { id: 'analytics', label: 'By Type', icon: BarChart3 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-orange-100 text-orange-700' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Hazards Tab */}
      {activeTab === 'hazards' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search hazards..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="border rounded px-3 py-2 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="border rounded px-3 py-2 text-sm"
              >
                <option value="all">All Types</option>
                {Object.entries(HAZARD_TYPES).map(([key, val]) => (
                  <option key={key} value={key}>{val.icon} {val.label}</option>
                ))}
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {filteredHazards.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-3" />
                <p className="text-gray-500">No hazards found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredHazards.map(hazard => {
                  const typeConfig = HAZARD_TYPES[hazard.type] || HAZARD_TYPES.other;
                  return (
                    <div key={hazard.id} className="border rounded-lg p-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setExpandedHazard(expandedHazard === hazard.id ? null : hazard.id)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{typeConfig.icon}</span>
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                              <Badge variant="outline" className={
                                hazard.status === 'active' ? 'text-green-600' :
                                hazard.status === 'pending' ? 'text-orange-600' : 'text-gray-600'
                              }>
                                {hazard.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{hazard.description || 'No description'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {hazard.status === 'pending' && (
                            <Button size="sm" onClick={(e) => { e.stopPropagation(); handleApprove(hazard.id); }}
                              className="bg-green-500 hover:bg-green-600">
                              <CheckCircle className="h-4 w-4 mr-1" /> Approve
                            </Button>
                          )}
                          {hazard.status === 'active' && (
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleExpire(hazard.id); }}>
                              <EyeOff className="h-4 w-4 mr-1" /> Expire
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleDelete(hazard.id); }}
                            className="text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {expandedHazard === hazard.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </div>
                      {expandedHazard === hazard.id && (
                        <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Location</p>
                            <p className="font-medium">{hazard.location || `${hazard.latitude?.toFixed(4)}, ${hazard.longitude?.toFixed(4)}`}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Reported</p>
                            <p className="font-medium">{formatDate(hazard.reportedAt)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Expires</p>
                            <p className="font-medium">{hazard.expiresAt ? `${formatDate(hazard.expiresAt)} (${getTimeRemaining(hazard.expiresAt)})` : 'No expiry'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Reporter</p>
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">{hazard.reportedBy || 'Unknown'}</code>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top Reporters Tab */}
      {activeTab === 'reporters' && (
        <Card>
          <CardContent className="p-4">
            {!stats?.topReporters || stats.topReporters.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p className="text-gray-500">No reporters yet</p>
                <p className="text-sm text-gray-400 mt-1">Top hazard reporters will appear here once users submit reports</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.topReporters.map((reporter, i) => (
                  <div key={reporter.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        i === 0 ? 'bg-yellow-100 text-yellow-700' :
                        i === 1 ? 'bg-gray-100 text-gray-600' :
                        i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-50 text-gray-500'
                      }`}>
                        {i + 1}
                      </div>
                      <code className="text-sm">{reporter.userId.substring(0, 16)}...</code>
                    </div>
                    <Badge variant="outline">{reporter.count} reports</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <Card>
          <CardContent className="p-4">
            <div className="space-y-3">
              {Object.entries(HAZARD_TYPES).map(([type, config]) => {
                const count = stats?.byType[type] || 0;
                const percent = stats?.total ? (count / stats.total) * 100 : 0;
                return (
                  <div key={type} className="flex items-center gap-4">
                    <span className="text-xl w-8">{config.icon}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>{config.label}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full ${config.color.replace('text-', 'bg-').replace('-800', '-500')}`}
                          style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
